import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import Footer from "@/components/Footer";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Users, BadgeCheck, Sparkles, ArrowLeft, Download, FileDown, ShieldCheck } from "lucide-react";

export default function Storefront() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get(`/creators/${handle}`).then((r) => setData(r.data)).catch(() => setData(false)).finally(() => setLoading(false));
  }, [handle]);

  const buy = async (id, type) => {
    if (!user) {
      toast.info("Sign in to continue.");
      navigate("/auth?role=member");
      return;
    }
    setBusy(id);
    try {
      const { data: res } = await api.post("/payments/checkout", { item_id: id, item_type: type, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout.");
      setBusy(null);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center"><Loader2 className="w-8 h-8 text-violet-600 animate-spin" /></div>;

  if (!data) return (
    <div className="min-h-screen bg-[#FAF9F6] grain flex flex-col items-center justify-center px-4">
      <h1 className="font-display text-3xl font-extrabold mb-2">Creator not found</h1>
      <p className="text-slate-600 mb-6">This storefront doesn't exist or was removed.</p>
      <Button onClick={() => navigate("/")} className="bg-violet-600 hover:bg-violet-500 text-white btn-pop rounded-full"><ArrowLeft className="w-4 h-4 mr-2" /> Back home</Button>
    </div>
  );

  const c = data.creator;
  const banner = c.banner_url || "https://images.unsplash.com/photo-1673767298248-b128f17f89af?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#111114] grain">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Logo />
        <Button variant="ghost" className="font-semibold hover:bg-violet-100" onClick={() => navigate(user ? (user.role === "creator" ? "/creator" : "/member") : "/auth")} data-testid="storefront-nav-btn">
          {user ? "Dashboard" : "Log in"}
        </Button>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="card-pop overflow-hidden mt-2">
          <div className="h-44 sm:h-56 bg-cover bg-center" style={{ backgroundImage: `url(${banner})` }} />
        </div>
        <div className="px-2 sm:px-6 flex flex-col items-center text-center -mt-14">
          <div className="w-28 h-28 rounded-2xl border-2 border-[#111114] overflow-hidden bg-violet-300 flex items-center justify-center shadow-[5px_5px_0_0_#111114]">
            {c.avatar_url ? <img src={c.avatar_url} alt={c.display_name} className="w-full h-full object-cover" /> : <span className="font-display text-4xl font-extrabold">{(c.display_name || "?")[0]}</span>}
          </div>
          <div className="flex items-center gap-2 mt-4">
            <h1 className="font-display text-3xl font-extrabold" data-testid="storefront-name">{c.display_name}</h1>
            <BadgeCheck className="w-6 h-6 text-violet-600" />
          </div>
          <p className="text-violet-600 font-mono text-sm mt-1">@{c.handle}</p>
          {c.category && <span className="chip bg-cyan-200 mt-3 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> {c.category}</span>}
          {c.bio && <p className="text-slate-600 max-w-xl mt-4 leading-relaxed">{c.bio}</p>}
          <div className="flex items-center gap-2 mt-4 text-sm font-semibold text-slate-600">
            <Users className="w-4 h-4 text-violet-600" /> <span className="font-mono text-[#111114]">{data.subscriber_count}</span> subscribers
          </div>
        </div>
      </div>

      {/* Tiers */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-display text-2xl font-extrabold text-center mb-8">Choose your membership</h2>
        {data.tiers.length === 0 ? <p className="text-center text-slate-500">No tiers published yet.</p> : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.tiers.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                className={`card-pop p-7 flex flex-col ${i === 1 ? "bg-violet-600 text-white" : "bg-white"}`} data-testid={`tier-card-${t.id}`}>
                {i === 1 && <span className="chip bg-lime-300 text-[#111114] self-start mb-3">Popular</span>}
                <h3 className="font-display text-xl font-extrabold">{t.name}</h3>
                <div className="mt-3 mb-1">
                  <span className="font-display text-4xl font-extrabold">{money(t.price, t.currency)}</span>
                  <span className={i === 1 ? "text-violet-200 text-sm" : "text-slate-500 text-sm"}>/mo</span>
                </div>
                {t.description && <p className={`text-sm mb-5 ${i === 1 ? "text-violet-100" : "text-slate-600"}`}>{t.description}</p>}
                <ul className="space-y-2.5 mb-7 flex-1">
                  {(t.benefits || []).map((b, bi) => (
                    <li key={bi} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${i === 1 ? "text-lime-300" : "text-violet-600"}`} strokeWidth={3} /> {b}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => buy(t.id, "tier")} disabled={busy === t.id}
                  className={`w-full btn-pop rounded-full h-11 font-bold ${i === 1 ? "bg-lime-300 text-[#111114] hover:bg-lime-200" : "bg-violet-600 text-white hover:bg-violet-500"}`}
                  data-testid={`subscribe-btn-${t.id}`}>
                  {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Products */}
      {data.products.length > 0 && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="font-display text-2xl font-extrabold text-center mb-8 flex items-center justify-center gap-2">
            <FileDown className="w-6 h-6 text-violet-600" /> Digital downloads
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.products.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="card-pop card-pop-hover bg-white overflow-hidden flex flex-col" data-testid={`product-card-${p.id}`}>
                <div className="h-36 bg-cover bg-center border-b-2 border-[#111114] relative" style={{ backgroundImage: `url(${p.thumbnail_url || "https://images.unsplash.com/photo-1554056878-6911635b1573?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"})` }}>
                  <span className="chip bg-cyan-200 absolute top-3 left-3">{p.file_type}</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display font-extrabold text-lg">{p.name}</h3>
                  {p.description && <p className="text-slate-600 text-sm mt-1 mb-4 flex-1">{p.description}</p>}
                  <div className="flex items-center justify-between mt-auto">
                    <span className="font-display text-2xl font-extrabold">{money(p.price, p.currency)}</span>
                    <Button onClick={() => buy(p.id, "product")} disabled={busy === p.id}
                      className="bg-violet-600 text-white btn-pop rounded-full h-10 font-bold hover:bg-violet-500" data-testid={`buy-btn-${p.id}`}>
                      {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> Buy</>}
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-12">
        <p className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-500" data-testid="stripe-trust-note">
          <ShieldCheck className="w-4 h-4 text-violet-600" /> Payments are securely processed by Stripe
        </p>
      </div>
      <Footer />
    </div>
  );
}
